import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DeviceContextService } from '../device-context.service';
import { CloudApiService } from '../../../services/cloud-api.service';

interface EnvironmentPreset {
    label: string;
    value: string;
    host: string | null; // null = custom
    secure: boolean;
}

const ENVIRONMENT_PRESETS: EnvironmentPreset[] = [
    { label: 'Production (proav.cloud)', value: 'production', host: 'proav.cloud', secure: true },
    { label: 'Test (test.proav.cloud)', value: 'test', host: 'test.proav.cloud', secure: true },
    { label: 'Custom…', value: 'custom', host: null, secure: true }
];

@Component({
    selector: 'app-migrate-cloud-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DialogModule, InputTextModule, SelectModule],
    templateUrl: './migrate-cloud-dialog.html',
    styleUrl: './migrate-cloud-dialog.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MigrateCloudDialog {
    private ctx = inject(DeviceContextService);
    private api = inject(CloudApiService);

    readonly visible = model(false);

    readonly presets = ENVIRONMENT_PRESETS;
    readonly selectedPreset = signal<string>('production');
    readonly host = signal<string>('proav.cloud');
    readonly secure = signal<boolean>(true);
    readonly apiKey = signal<string>('');
    readonly newDeviceName = signal<string>('');
    readonly confirmName = signal<string>('');
    readonly submitting = signal(false);
    readonly error = signal<string | null>(null);

    readonly deviceName = computed(() => this.ctx.device()?.name ?? '');
    readonly isCustom = computed(() => this.selectedPreset() === 'custom');

    readonly validationError = computed<string | null>(() => {
        const host = this.host().trim();
        if (!host) return 'Target host is required.';
        if (/^https?:\/\//i.test(host)) return 'Host must not include http:// or https://.';
        if (host.includes('/')) return 'Host must not include a path — use host or host:port.';
        const apiKey = this.apiKey().trim();
        if (!apiKey) return 'A tenant API key for the target environment is required.';
        if (!apiKey.startsWith('proav_')) return 'That does not look like a tenant API key (proav_…).';
        return null;
    });

    readonly confirmed = computed(() => {
        const name = this.deviceName();
        return !!name && this.confirmName().trim() === name;
    });

    readonly canSubmit = computed(() => !this.submitting() && !this.validationError() && this.confirmed());

    onPresetChange(value: string): void {
        this.selectedPreset.set(value);
        const preset = this.presets.find(p => p.value === value);
        if (preset?.host) {
            this.host.set(preset.host);
            this.secure.set(preset.secure);
        } else {
            this.host.set('');
        }
    }

    async migrate(): Promise<void> {
        const deviceId = this.ctx.device()?.id;
        if (!deviceId || !this.canSubmit()) return;

        this.submitting.set(true);
        this.error.set(null);
        try {
            const payload: Record<string, unknown> = {
                host: this.host().trim(),
                secure: this.secure(),
                apiKey: this.apiKey().trim()
            };
            const newName = this.newDeviceName().trim();
            if (newName) payload['deviceName'] = newName;

            await this.api.createDeviceAction(deviceId, {
                actionType: 'MigrateCloud',
                payload
            });
            this.reset();
            this.visible.set(false);
        } catch (err) {
            this.error.set(this.toErrorMessage(err));
        } finally {
            this.submitting.set(false);
        }
    }

    reset(): void {
        this.selectedPreset.set('production');
        this.host.set('proav.cloud');
        this.secure.set(true);
        this.apiKey.set('');
        this.newDeviceName.set('');
        this.confirmName.set('');
        this.error.set(null);
    }

    private toErrorMessage(err: any): string {
        if (!err) return 'Unknown error';
        if (typeof err === 'string') return err;
        if (err.error) {
            if (typeof err.error === 'string') return err.error;
            if (err.error?.error) return err.error.error;
            if (err.error?.message) return err.error.message;
        }
        if (err.message) return err.message;
        return 'Request failed';
    }
}
